import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp,
  increment,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Search Analytics Types
export interface SearchQuery {
  id: string;
  userId?: string;
  query: string;
  filters: SearchFilters;
  resultsCount: number;
  timestamp: any;
  clickedResults?: string[];
}

export interface SearchFilters {
  contentType?: string[];
  genres?: string[];
  yearRange?: { min: number; max: number };
  durationRange?: { min: number; max: number };
  language?: string[];
  rating?: string[];
  sortBy?: 'relevance' | 'newest' | 'oldest' | 'popular' | 'rating';
}

export interface TrendingContent {
  id: string;
  title: string;
  category: string;
  poster: string;
  views: number;
  likes: number;
  trendingScore: number;
  period: 'daily' | 'weekly' | 'monthly';
  rank: number;
}

export interface Recommendation {
  id: string;
  title: string;
  category: string;
  poster: string;
  reason: string;
  score: number;
  type: 'similar' | 'trending' | 'popular' | 'recent';
}

export interface SearchSuggestion {
  id: string;
  text: string;
  type: 'query' | 'content' | 'creator';
  count: number;
  category?: string;
}

export interface SearchAnalytics {
  totalSearches: number;
  uniqueUsers: number;
  popularQueries: { query: string; count: number }[];
  popularFilters: { filter: string; count: number }[];
  averageResultsPerQuery: number;
  clickThroughRate: number;
  topCategories: { category: string; count: number }[];
}

// Search Discovery Service
export class SearchDiscoveryService {
  // Advanced Search with Filters
  static async searchContent(
    searchQuery: string,
    filters: SearchFilters = {},
    userId?: string,
    limitCount: number = 50
  ): Promise<any[]> {
    try {
      // Build the base query
      let q = query(collection(db, 'content'));
      
      // Apply content type filter first (if specified)
      if (filters.contentType && filters.contentType.length > 0) {
        q = query(q, where('category', 'in', filters.contentType));
      }
      
      // Apply genre filter
      if (filters.genres && filters.genres.length > 0) {
        q = query(q, where('genres', 'array-contains-any', filters.genres));
      }
      
      // Apply year range filter
      if (filters.yearRange) {
        q = query(q, where('year', '>=', filters.yearRange.min), where('year', '<=', filters.yearRange.max));
      }
      
      // Apply language filter
      if (filters.language && filters.language.length > 0) {
        q = query(q, where('language', 'in', filters.language));
      }
      
      // Apply rating filter
      if (filters.rating && filters.rating.length > 0) {
        q = query(q, where('contentRating', 'in', filters.rating));
      }
      
      // Apply sorting
      switch (filters.sortBy) {
        case 'newest':
          q = query(q, orderBy('createdAt', 'desc'));
          break;
        case 'oldest':
          q = query(q, orderBy('createdAt', 'asc'));
          break;
        case 'popular':
          q = query(q, orderBy('views', 'desc'));
          break;
        case 'rating':
          q = query(q, orderBy('rating', 'desc'));
          break;
        default:
          q = query(q, orderBy('createdAt', 'desc'));
      }
      
      // Fetch more results than needed for client-side filtering
      q = query(q, limit(limitCount * 3));
      
      const snapshot = await getDocs(q);
      let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Apply text search client-side for better matching (case-insensitive, partial word matching)
      if (searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase().trim();
        const searchTerms = searchLower.split(/\s+/).filter(term => term.length > 0);
        
        results = results.filter((item: any) => {
          const title = (item.title || '').toLowerCase();
          const description = (item.description || '').toLowerCase();
          const tags = (item.tags || []).map((tag: string) => tag.toLowerCase());
          const genres = (item.genres || []).map((genre: string) => genre.toLowerCase());
          
          // Check if any search term matches in title, description, tags, or genres
          return searchTerms.some(term => 
            title.includes(term) || 
            description.includes(term) ||
            tags.some((tag: string) => tag.includes(term)) ||
            genres.some((genre: string) => genre.includes(term))
          );
        });
      }
      
      // Limit results after filtering
      results = results.slice(0, limitCount);
      
      // Log search analytics
      if (userId) {
        await this.logSearchQuery(userId, searchQuery, filters, results.length);
      }
      
      return results;
    } catch (error) {
      console.error('Error searching content:', error);
      throw error;
    }
  }

  // Get Trending Content
  static async getTrendingContent(period: 'daily' | 'weekly' | 'monthly' = 'weekly', limitCount: number = 20): Promise<TrendingContent[]> {
    try {
      const now = new Date();
      let startDate: Date;
      
      switch (period) {
        case 'daily':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'weekly':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'monthly':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
      }
      
      // Get content with high view counts in the specified period
      const q = query(
        collection(db, 'content'),
        where('createdAt', '>=', startDate),
        orderBy('views', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const trending = snapshot.docs.map((doc, index) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          category: data.category,
          poster: data.poster,
          views: data.views || 0,
          likes: data.likes || 0,
          trendingScore: this.calculateTrendingScore(data.views || 0, data.likes || 0, data.createdAt),
          period,
          rank: index + 1
        };
      });
      
      return trending;
    } catch (error) {
      console.error('Error getting trending content:', error);
      throw error;
    }
  }

  // Get Content Recommendations
  static async getRecommendations(userId: string, limitCount: number = 10): Promise<Recommendation[]> {
    try {
      // Get user's watch history and preferences
      const userWatchlist = await this.getUserWatchlist(userId);
      const userPreferences = await this.getUserPreferences(userId);
      
      const recommendations: Recommendation[] = [];
      
      // Similar content recommendations
      if (userWatchlist.length > 0) {
        const similarContent = await this.getSimilarContent(userWatchlist[0].id, limitCount);
        recommendations.push(...similarContent.map(content => ({
          id: content.id,
          title: content.title,
          category: content.category,
          poster: content.poster,
          reason: 'Similar to your watchlist',
          score: 0.8,
          type: 'similar' as const
        })));
      }
      
      // Trending recommendations
      const trending = await this.getTrendingContent('weekly', 5);
      recommendations.push(...trending.map(content => ({
        id: content.id,
        title: content.title,
        category: content.category,
        poster: content.poster,
        reason: 'Trending this week',
        score: 0.7,
        type: 'trending' as const
      })));
      
      // Popular content recommendations
      const popular = await this.getPopularContent(5);
      recommendations.push(...popular.map(content => ({
        id: content.id,
        title: content.title,
        category: content.category,
        poster: content.poster,
        reason: 'Popular with viewers',
        score: 0.6,
        type: 'popular' as const
      })));
      
      // Sort by score and return top recommendations
      return recommendations
        .sort((a, b) => b.score - a.score)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting recommendations:', error);
      throw error;
    }
  }

  // Get Search Suggestions
  static async getSearchSuggestions(query: string, limitCount: number = 10): Promise<SearchSuggestion[]> {
    try {
      if (!query.trim()) return [];
      
      const suggestions: SearchSuggestion[] = [];
      
      // Get popular search queries that start with the input
      const searchQueries = await this.getPopularSearchQueries(query, limitCount);
      suggestions.push(...searchQueries.map((q, index) => ({
        id: `query_${index}`,
        text: q.query,
        type: 'query' as const,
        count: q.count
      })));
      
      // Get content titles that match
      const contentSuggestions = await this.getContentSuggestions(query, limitCount);
      suggestions.push(...contentSuggestions.map((content, index) => ({
        id: `content_${content.id}`,
        text: content.title,
        type: 'content' as const,
        count: content.views || 0,
        category: content.category
      })));
      
      return suggestions.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      throw error;
    }
  }

  // Log Search Query for Analytics
  static async logSearchQuery(
    userId: string,
    query: string,
    filters: SearchFilters,
    resultsCount: number
  ): Promise<void> {
    try {
      const searchData: Omit<SearchQuery, 'id'> = {
        userId,
        query,
        filters,
        resultsCount,
        timestamp: serverTimestamp()
      };
      
      await addDoc(collection(db, 'searchQueries'), searchData);
    } catch (error) {
      console.error('Error logging search query:', error);
    }
  }

  // Track Search Result Click
  static async trackSearchClick(
    userId: string,
    searchQuery: string,
    contentId: string,
    position: number
  ): Promise<void> {
    try {
      // Find the search query and update it with the clicked result
      const searchQueriesRef = collection(db, 'searchQueries');
      const q = query(
        searchQueriesRef,
        where('userId', '==', userId),
        where('query', '==', searchQuery),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const searchQueryDoc = snapshot.docs[0];
        await updateDoc(doc(db, 'searchQueries', searchQueryDoc.id), {
          clickedResults: arrayUnion({
            contentId,
            position,
            timestamp: serverTimestamp()
          })
        });
      }
    } catch (error) {
      console.error('Error tracking search click:', error);
    }
  }

  // Get Search Analytics
  static async getSearchAnalytics(): Promise<SearchAnalytics> {
    try {
      const searchQueriesRef = collection(db, 'searchQueries');
      const snapshot = await getDocs(searchQueriesRef);
      
      const queries = snapshot.docs.map(doc => doc.data() as SearchQuery);
      
      // Calculate analytics
      const totalSearches = queries.length;
      const uniqueUsers = new Set(queries.map(q => q.userId).filter(Boolean)).size;
      
      // Popular queries
      const queryCounts: { [key: string]: number } = {};
      queries.forEach(q => {
        queryCounts[q.query] = (queryCounts[q.query] || 0) + 1;
      });
      const popularQueries = Object.entries(queryCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([query, count]) => ({ query, count }));
      
      // Popular filters
      const filterCounts: { [key: string]: number } = {};
      queries.forEach(q => {
        Object.entries(q.filters).forEach(([key, value]) => {
          if (value && (Array.isArray(value) ? value.length > 0 : true)) {
            filterCounts[key] = (filterCounts[key] || 0) + 1;
          }
        });
      });
      const popularFilters = Object.entries(filterCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([filter, count]) => ({ filter, count }));
      
      const averageResultsPerQuery = queries.reduce((sum, q) => sum + q.resultsCount, 0) / queries.length;
      
      // Calculate click-through rate
      const queriesWithClicks = queries.filter(q => q.clickedResults && q.clickedResults.length > 0);
      const clickThroughRate = queriesWithClicks.length / queries.length;
      
      return {
        totalSearches,
        uniqueUsers,
        popularQueries,
        popularFilters,
        averageResultsPerQuery,
        clickThroughRate,
        topCategories: [] // Would need to analyze content categories
      };
    } catch (error) {
      console.error('Error getting search analytics:', error);
      throw error;
    }
  }

  // Helper Methods
  private static calculateTrendingScore(views: number, likes: number, createdAt: any): number {
    const timeDecay = Math.max(0, 1 - (Date.now() - createdAt.toMillis()) / (7 * 24 * 60 * 60 * 1000));
    return (views * 0.7 + likes * 0.3) * timeDecay;
  }

  private static async getUserWatchlist(userId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'watchlist'),
        where('userId', '==', userId),
        limit(10)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error getting user watchlist:', error);
      return [];
    }
  }

  private static async getUserPreferences(userId: string): Promise<any> {
    try {
      const userDoc = await getDocs(query(collection(db, 'userProfiles'), where('uid', '==', userId)));
      if (!userDoc.empty) {
        return userDoc.docs[0].data();
      }
      return {};
    } catch (error) {
      console.error('Error getting user preferences:', error);
      return {};
    }
  }

  private static async getSimilarContent(contentId: string, limitCount: number): Promise<any[]> {
    try {
      // Get the content to find similar items
      const contentDoc = await getDocs(query(collection(db, 'content'), where('__name__', '==', contentId)));
      if (contentDoc.empty) return [];
      
      const content = contentDoc.docs[0].data();
      
      // Find content with similar genres
      const q = query(
        collection(db, 'content'),
        where('genres', 'array-contains-any', content.genres || []),
        where('__name__', '!=', contentId),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting similar content:', error);
      return [];
    }
  }

  private static async getPopularContent(limitCount: number): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'content'),
        orderBy('views', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting popular content:', error);
      return [];
    }
  }

  private static async getPopularSearchQueries(searchQuery: string, limitCount: number): Promise<{ query: string; count: number }[]> {
    try {
      const q = query(
        collection(db, 'searchQueries'),
        where('query', '>=', searchQuery),
        where('query', '<=', searchQuery + '\uf8ff'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const queryCounts: { [key: string]: number } = {};
      
      snapshot.docs.forEach(doc => {
        const data = doc.data() as any;
        queryCounts[data.query] = (queryCounts[data.query] || 0) + 1;
      });
      
      return Object.entries(queryCounts)
        .map(([query, count]) => ({ query, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting popular search queries:', error);
      return [];
    }
  }

  private static async getContentSuggestions(searchQuery: string, limitCount: number): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'content'),
        where('title', '>=', searchQuery),
        where('title', '<=', searchQuery + '\uf8ff'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) }));
    } catch (error) {
      console.error('Error getting content suggestions:', error);
      return [];
    }
  }
}
